import React, {
  useState,
  useEffect,
  forwardRef,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { testsAPI } from '../services/api';
import { styled } from '@mui/material/styles';
import Button from './UIKit/Button';
import Card from './UIKit/Card';
import { useTranslation } from 'react-i18next';

// Стилизованные компоненты
const TestContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  overflow: 'hidden',
}));


const TestDetail = forwardRef(
  (
    {
      courseId,
      moduleId,
      testId,
      onBackClick,
      onNextTest,
      onPrevTest,
      hasNextTest,
      hasPrevTest,
      nextItemType,
      prevItemType,
    },
    ref
  ) => {
    const { t } = useTranslation(['tests', 'common']);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [test, setTest] = useState(null);

    // Загрузка данных теста
    useEffect(() => {
      const fetchTest = async () => {
        try {
          setLoading(true);
          setError(null);

          const response = await testsAPI.getById(testId);
          setTest(response.data);

        } catch (err) {
          console.error('Ошибка при загрузке теста:', err);
          setError(
            err.response?.data?.message || t('testDetail.errors.loadError')
          );
        } finally {
          setLoading(false);
        }
      };

      fetchTest();
    }, [courseId, moduleId, testId, t]);


    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={onBackClick}>
            {t('testDetail.navigation.backToCourse')}
          </Button>
        </Box>
      );
    }

    if (!test) {
      return (
        <Box sx={{ my: 4 }}>
          <Alert severity="warning">{t('testDetail.errors.notFound')}</Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={onBackClick}>
            {t('testDetail.navigation.backToCourse')}
          </Button>
        </Box>
      );
    }

    // Рендер вариантов ответа в зависимости от типа вопроса (точно как в TestView.js)
    const renderQuestionOptions = (question) => {
      switch (question.type) {
        case 'single-choice':
          return (
            <RadioGroup disabled>
              {question.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option.text}
                  control={
                    <Radio 
                      checked={option.isCorrect || option.correct}
                      disabled
                      sx={{ 
                        color: (option.isCorrect || option.correct) ? 'success.main' : 'default',
                        '&.Mui-checked': {
                          color: 'success.main',
                        },
                      }}
                    />
                  }
                  label={option.text}
                  sx={{
                    bgcolor: (option.isCorrect || option.correct) ? 'success.50' : 'transparent',
                    border: (option.isCorrect || option.correct) ? '1px solid' : 'none',
                    borderColor: (option.isCorrect || option.correct) ? 'success.main' : 'transparent',
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                  }}
                />
              ))}
            </RadioGroup>
          );

        case 'multiple-choice':
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {question.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox 
                      checked={option.isCorrect || option.correct}
                      disabled
                      sx={{ 
                        color: (option.isCorrect || option.correct) ? 'success.main' : 'default',
                        '&.Mui-checked': {
                          color: 'success.main',
                        },
                      }}
                    />
                  }
                  label={option.text}
                  sx={{
                    bgcolor: (option.isCorrect || option.correct) ? 'success.50' : 'transparent',
                    border: (option.isCorrect || option.correct) ? '1px solid' : 'none',
                    borderColor: (option.isCorrect || option.correct) ? 'success.main' : 'transparent',
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                  }}
                />
              ))}
            </Box>
          );

        case 'true-false':
          return (
            <RadioGroup disabled>
              <FormControlLabel
                value="true"
                control={
                  <Radio 
                    checked={question.correctAnswer === 'true'}
                    disabled
                    sx={{ 
                      color: question.correctAnswer === 'true' ? 'success.main' : 'default',
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={t('common:status.true')}
                sx={{
                  bgcolor: question.correctAnswer === 'true' ? 'success.50' : 'transparent',
                  border: question.correctAnswer === 'true' ? '1px solid' : 'none',
                  borderColor: question.correctAnswer === 'true' ? 'success.main' : 'transparent',
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              />
              <FormControlLabel
                value="false"
                control={
                  <Radio 
                    checked={question.correctAnswer === 'false'}
                    disabled
                    sx={{ 
                      color: question.correctAnswer === 'false' ? 'success.main' : 'default',
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={t('common:status.false')}
                sx={{
                  bgcolor: question.correctAnswer === 'false' ? 'success.50' : 'transparent',
                  border: question.correctAnswer === 'false' ? '1px solid' : 'none',
                  borderColor: question.correctAnswer === 'false' ? 'success.main' : 'transparent',
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              />
            </RadioGroup>
          );

        case 'text':
          return (
            <Box
              sx={{
                p: 2,
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('testDetail.questions.textAnswer')}:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {question.correctAnswer || t('testDetail.questions.noAnswer')}
              </Typography>
            </Box>
          );

        case 'memory':
          return (
            <Box
              sx={{
                p: 2,
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('testDetail.questions.memoryGame')}:
              </Typography>
              {question.options?.reduce((pairs, option, index) => {
                if (option.pairId) {
                  const existingPair = pairs.find(pair => pair.pairId === option.pairId);
                  if (existingPair) {
                    existingPair.cards.push(option);
                  } else {
                    pairs.push({ pairId: option.pairId, cards: [option] });
                  }
                } else {
                  pairs.push({ pairId: `single-${index}`, cards: [option] });
                }
                return pairs;
              }, []).map((pair, pairIndex) => (
                <Typography key={pairIndex} variant="body2" sx={{ mb: 0.5 }}>
                  • {pair.cards[0]?.text || 'Card 1'} ↔ {pair.cards[1]?.text || 'Card 2'}
                </Typography>
              ))}
            </Box>
          );

        case 'open-answer':
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'info.50',
                  border: '1px dashed',
                  borderColor: 'info.main',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('tests:view.openAnswerHint')}
                </Typography>
              </Box>
              {question.correctAnswer && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'warning.50',
                    border: '1px solid',
                    borderColor: 'warning.light',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="warning.dark" fontWeight={600} display="block" mb={0.5}>
                    {t('tests:view.evaluationCriteria')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {question.correctAnswer}
                  </Typography>
                </Box>
              )}
            </Box>
          );

        default:
          return (
            <Typography color="error">{t('tests:view.unknownType')}</Typography>
          );
      }
    };

    return (
      <TestContainer elevation={0}>
        {/* Верхняя панель с навигацией */}
        <Box
          className="test-header"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          {onBackClick && (
            <Button
              onClick={onBackClick}
              variant="neutral"
              startIcon={<ArrowBackIcon />}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {t('testDetail.navigation.backToCourse')}
              </Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {t('testDetail.navigation.back')}
              </Box>
            </Button>
          )}
        </Box>

        {/* Навигация между тестами - отображается только если мы в контексте курса */}
        {courseId && (
          <Box
            className="top-navigation"
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
          >
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={onPrevTest}
              disabled={!hasPrevTest}
              sx={{
                minWidth: { xs: '40px', sm: 'auto' },
                '& .MuiButton-startIcon': {
                  margin: { xs: '0', sm: '-4px 8px -4px 0' },
                },
                '& .MuiButton-endIcon': {
                  margin: { xs: '0', sm: '-4px 0 -4px 8px' },
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {prevItemType === 'lesson' 
                  ? t('testDetail.navigation.prevLesson')
                  : t('testDetail.navigation.prevTest')
                }
              </Box>
            </Button>

            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={onNextTest}
              disabled={!hasNextTest}
              sx={{
                minWidth: { xs: '40px', sm: 'auto' },
                '& .MuiButton-startIcon': {
                  margin: { xs: '0', sm: '-4px 8px -4px 0' },
                },
                '& .MuiButton-endIcon': {
                  margin: { xs: '0', sm: '-4px 0 -4px 8px' },
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {nextItemType === 'lesson' 
                  ? t('testDetail.navigation.nextLesson')
                  : t('testDetail.navigation.nextTest')
                }
              </Box>
            </Button>
          </Box>
        )}

        <Box className="test-info">
          {/* Заголовок теста - адаптивный размер */}
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              lineHeight: { xs: 1.3, sm: 1.4 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            {test.title}
          </Typography>

          {/* Описание теста */}
          {test.description && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              {test.description}
            </Typography>
          )}

          {/* Информация о тесте в виде карточек */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <QuizIcon color="primary" sx={{ fontSize: '2rem', mb: 1 }} />
              <Typography variant="h6" color="primary">
                {test.questions?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('testDetail.content.titles.questions')}
              </Typography>
            </Card>

            {test.timeLimit && (
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <AccessTimeIcon color="primary" sx={{ fontSize: '2rem', mb: 1 }} />
                <Typography variant="h6" color="primary">
                  {test.timeLimit}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('testDetail.content.titles.minutes')}
                </Typography>
              </Card>
            )}

            {test.passingScore && (
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <CheckCircleIcon color="primary" sx={{ fontSize: '2rem', mb: 1 }} />
                <Typography variant="h6" color="primary">
                  {test.passingScore}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('testDetail.content.titles.passingScore')}
                </Typography>
              </Card>
            )}

          </Box>
        </Box>

        {/* Вопросы теста */}
        <Box sx={{ width: '100%', mb: 3 }}>
          {test.questions?.map((question, questionIndex) => (
            <Card key={questionIndex} sx={{ mb: 3, borderRadius: '10px' }}>
              <Box sx={{ p: 2 }}>
                {/* Question Options - Following TestView.js structure */}
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">
                    <Typography variant="h6" gutterBottom>
                      {question.question}
                    </Typography>
                  </FormLabel>

                  {renderQuestionOptions(question)}
                </FormControl>
              </Box>
            </Card>
          ))}

          {(!test.questions || test.questions.length === 0) && (
            <Alert severity="info">
              {t('testDetail.questions.noQuestions')}
            </Alert>
          )}
        </Box>

        {/* Нижняя навигация */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
            mt: 4,
            gap: 1,
          }}
        >
          {onBackClick && (
            <Button
              variant="outlined"
              onClick={onBackClick}
              fullWidth
              sx={{
                width: { xs: '100%', lg: 'fit-content' },
              }}
            >
              {t('testDetail.navigation.backToCourse')}
            </Button>
          )}

          {courseId && (
            <Box
              className="bottom-navigation"
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                maxWidth: { xs: '100%', lg: 'fit-content' },
              }}
            >
              {hasPrevTest && (
                <Button
                  variant="outlined"
                  startIcon={<NavigateBeforeIcon />}
                  onClick={onPrevTest}
                  fullWidth
                  sx={{
                    width: { xs: '100%', lg: 'fit-content' },
                  }}
                >
                  {prevItemType === 'lesson' 
                    ? t('testDetail.navigation.prevLesson')
                    : t('testDetail.navigation.prevTest')
                  }
                </Button>
              )}

              {hasNextTest && (
                <Button
                  variant="contained"
                  endIcon={<NavigateNextIcon />}
                  onClick={onNextTest}
                  color="primary"
                  fullWidth
                  sx={{
                    width: { xs: '100%', lg: 'fit-content' },
                  }}
                >
                  {nextItemType === 'lesson' 
                    ? t('testDetail.navigation.nextLesson')
                    : t('testDetail.navigation.nextTest')
                  }
                </Button>
              )}
            </Box>
          )}
        </Box>
      </TestContainer>
    );
  }
);

export default TestDetail;
